import { MultiFactorAuthenticationError } from '@pba/client-auth';
import { type CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { AxiosError } from 'axios';
import { Col, Modal, Row, handleAxiosError, message } from 'ui';
import { getRailsAccessToken } from 'common/services';
import { useLoginToNode, useLoginWithSocial } from 'features/users';
import { useAuthenticateMutation, useHandleMfaError } from '../hooks';

type P = {
  getOrganizationId?: () => string | undefined;
  onNavigateToCheckCode: ({ tokenToGetCode, tokenToLogin }: { tokenToGetCode?: string; tokenToLogin?: string }) => void;
  onNavigateToSignUp?: () => void;
  onSuccessLogin: () => Promise<void>;
};

const onError = (error?: Error) => {
  const defaultErrorMessage = 'Failed to connect to Google account.';

  if (error) {
    handleAxiosError(error, defaultErrorMessage);
    console.error('Google Sign-in failed:', error);
  } else {
    message.error(defaultErrorMessage);
  }
};

export const SocialButtons = ({ getOrganizationId, onNavigateToCheckCode, onNavigateToSignUp, onSuccessLogin }: P) => {
  const { mutate: authenticate } = useAuthenticateMutation();
  const { mutateAsync: nodeLoginAsync } = useLoginToNode();
  const { mutateAsync: attemptWithSocial } = useLoginWithSocial();

  const [modal, contextHolder] = Modal.useModal();
  const { contextHolder: mfaContextHolder, handleError: handleMfaError } = useHandleMfaError({
    onNavigateToCheckCode,
    onSuccessLogin,
  });

  const onSuccess = (credentials: CredentialResponse) => {
    authenticate(
      {
        async nodeLogin() {
          const railsToken = await getRailsAccessToken();

          return nodeLoginAsync(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Token will be always defined
            { accessToken: railsToken!, strategy: 'pba' },
          );
        },
        async railsLogin() {
          return attemptWithSocial({
            code: credentials.credential,
            organizationId: getOrganizationId?.(),
          });
        },
      },
      {
        onError(error) {
          if (error instanceof MultiFactorAuthenticationError) {
            handleMfaError({ error });
          } else if (
            error instanceof AxiosError &&
            error.response?.data &&
            typeof error.response.data === 'object' &&
            'message' in error.response.data
          ) {
            if (onNavigateToSignUp && error.response.data.message === 'User cannot register without organization_id.') {
              modal.confirm({
                content:
                  'Account you are trying to log in to, doesn’t exist. Please proceed with Sign up and create your account first.',
                okText: 'Sign up',
                onOk() {
                  onNavigateToSignUp();
                },
                title: 'Account doesn’t exist',
              });
            } else {
              onError(error);
            }
          } else {
            onError(error);
          }
        },
        async onSuccess() {
          await onSuccessLogin();
        },
      },
    );
  };

  return (
    <>
      {contextHolder}
      {mfaContextHolder}

      <Row gutter={['ms', 0]} justify="center">
        {import.meta.env.VITE_APP_GOOGLE_CLIENT_ID ? (
          <Col>
            <GoogleLogin logo_alignment="center" onError={onError} onSuccess={onSuccess} />
          </Col>
        ) : null}
      </Row>
    </>
  );
};
